import {
    LessonMaterial,
    LessonMaterialInformation,
} from 'src/api/endpoints/lessons/lessons.types';
import React, {
    FC,
    ReactElement,
    RefObject,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { LessonMaterialAttachingForm } from 'src/pages/Attendant/components/Modal';
import { ReasonFunction, SingleParamFunction } from 'src/@types';
import { TeacherLessonCard } from 'src/components/NearestLesson/LessonForMarking';
import styles from './ActiveLessonsPage.module.scss';
import { ConfirmButton } from 'src/pages/Attendant/components/ConfirmButton';
import { StudentLayout } from 'src/components/Layout/student/StudentLayout';
import { notifyAboutException, notifyAboutSuccess } from 'src/utils/notify';
import { AttendanceTable } from 'src/pages/Attendant/components/Table';
import { FormSubmitter } from 'src/pages/Attendant/form/form.types';
import { FormItem } from 'src/pages/Attendant/utils/utils';
import { AuthorizedUserProps } from 'src/routes/components/PrivateRoute';
import { skipToken } from '@reduxjs/toolkit/query';
import { LegacyModal } from 'src/components/Modal/LegacyModal';
import { useNumberParam } from 'src/hooks/params';
import { Nullable } from 'primereact/ts-helpers';
import { Card } from 'src/components/Card/Card';
import { Helmet } from 'react-helmet-async';
import { Button } from 'primereact/button';
import Loader from 'src/components/Loader';
import { filter, map, size } from 'lodash';
import { Toast } from 'primereact/toast';
import classNames from 'classnames';
import {
    TeacherLesson,
    useGetLessonMaterialQuery,
    useGetTeacherActiveLessonsQuery,
    useUploadLessonMaterialMutation,
} from 'src/api/endpoints/lessons/lessons.api';
import {
    useFinishLessonMarkingMutation,
    useGetStudentsAttendanceByLessonQuery,
    useUpdateStudentAttendanceMutation,
} from 'src/api/endpoints/attendance';
import {
    AttendanceStatuses,
    RawStudentAttendance,
    StudentAttendance,
    UpdatingStudentAttendance,
    UpdatingStudentAttendanceBase,
} from 'src/@types/modules/attendance/attendance.types';
import dayjs, { Dayjs } from 'dayjs';

// TODO: Сепарировать компонент Таблицы посещаемости от страниц, минимизировать дубли
export const ActiveLessonsPage: FC<AuthorizedUserProps> = (
    props: AuthorizedUserProps
): ReactElement => {
    const toast: RefObject<Toast> = useRef<Toast>(null);

    const [isLessonMaterialModalVisible, setLessonMaterialModalVisible] =
        useState(false);

    const [finishMarking, { isLoading: isFinishing }] =
        useFinishLessonMarkingMutation();

    // region Get active teacher lessons
    const [lessons, setLessons] = useState<TeacherLesson<Dayjs>[]>([]);
    const {
        data: teacherLessonsResponse,
        isLoading: teacherLessonsResponseIsLoading,
    } = useGetTeacherActiveLessonsQuery();

    useEffect((): void => {
        if (teacherLessonsResponseIsLoading || !teacherLessonsResponse) return;

        setLessons(
            map(
                teacherLessonsResponse,
                (l: TeacherLesson): TeacherLesson<Dayjs> => ({
                    ...l,
                    startsAt: dayjs(l.startsAt),
                    endsAt: dayjs(l.endsAt),
                })
            )
        );
    }, [teacherLessonsResponseIsLoading, teacherLessonsResponse]);
    // endregion

    // region Selected Lesson
    const [selectedLesson, selectLesson] =
        useState<TeacherLesson<Dayjs> | null>(null);

    const [selectedLessonId, setSelectedLessonId] = useNumberParam<
        TeacherLesson<Dayjs>
    >({
        fieldName: 'lessonId',
        ref: selectedLesson,
    });

    useEffect((): void => {
        if (!teacherLessonsResponse || teacherLessonsResponseIsLoading) return;
        if (!lessons) return;

        const foundedLesson: Nullable<TeacherLesson<Dayjs>> = lessons.find(
            (lesson: TeacherLesson<Dayjs>): boolean =>
                lesson.id === selectedLessonId
        );

        if (!foundedLesson) return setSelectedLessonId(null);

        selectLesson(foundedLesson);
    }, [
        teacherLessonsResponseIsLoading,
        teacherLessonsResponse,
        selectedLessonId,
        lessons,
    ]);
    // endregion

    const [uploadLessonMaterial] = useUploadLessonMaterialMutation();

    // region LessonMaterial attach information
    const [lessonHasMaterial, setLessonHasMaterial] = useState<boolean>(false);
    const [lessonMaterial, setLessonMaterial] =
        useState<LessonMaterialInformation>({
            attachment: null,
            isAttached: false,
        });

    const {
        data: lessonAttachedMaterial,
        isLoading: isLessonAttachedMaterialIsLoading,
    } = useGetLessonMaterialQuery(
        selectedLesson
            ? {
                  path: { lessonId: selectedLesson.id },
              }
            : skipToken
    );

    useEffect((): void => {
        if (isLessonAttachedMaterialIsLoading || !lessonAttachedMaterial)
            return;

        setLessonMaterial(lessonAttachedMaterial);
        setLessonHasMaterial(lessonAttachedMaterial.isAttached);
    }, [isLessonAttachedMaterialIsLoading, lessonAttachedMaterial]);
    // endregion

    const [studentsAttendance, setStudentsAttendance] = useState<
        StudentAttendance[]
    >([]);

    const {
        data: studentsAttendanceResponse,
        isLoading: studentsAttendanceResponseIsLoading,
    } = useGetStudentsAttendanceByLessonQuery(
        selectedLesson ? { path: { lessonId: selectedLesson.id } } : skipToken
    );

    const canFinishLessonMarking: boolean = useMemo(() => {
        return studentsAttendance.every(
            (a: StudentAttendance) =>
                a.status.id !== AttendanceStatuses.NotMarked
        );
    }, [studentsAttendance]);

    const [updateStudentAttendance] = useUpdateStudentAttendanceMutation();

    useEffect((): void => {
        if (!studentsAttendanceResponse || studentsAttendanceResponseIsLoading)
            return;

        setStudentsAttendance(studentsAttendanceResponse);
    }, [studentsAttendanceResponse, studentsAttendanceResponseIsLoading]);

    const transformStudentAttendance = (
        attendance: RawStudentAttendance
    ): UpdatingStudentAttendance => {
        const partialAttendance: UpdatingStudentAttendanceBase = {
            status: attendance.status,
        };

        if (attendance.status === AttendanceStatuses.Absent) {
            return {
                ...partialAttendance,
                mark: 0,
                reward: 0,
                comment: null,
            };
        }

        return {
            ...partialAttendance,
            mark: attendance.mark,
            reward: attendance.reward,
            comment: attendance.comment,
        };
    };

    const onStudentAttendanceChanged: SingleParamFunction<
        RawStudentAttendance
    > = (attendance: RawStudentAttendance): void => {
        if (!selectedLesson) return;

        updateStudentAttendance({
            path: {
                lessonId: selectedLesson.id,
                attendanceId: attendance.id,
            },
            body: transformStudentAttendance(attendance),
        })
            .unwrap()
            .then((updatedStudentAttendance: StudentAttendance): void => {
                setStudentsAttendance(
                    (previous: StudentAttendance[]): StudentAttendance[] =>
                        previous.map(
                            (
                                attendance: StudentAttendance
                            ): StudentAttendance => {
                                if (
                                    attendance.id ===
                                    updatedStudentAttendance.id
                                ) {
                                    return updatedStudentAttendance;
                                }
                                return attendance;
                            }
                        )
                );
            })
            .catch((reason: any): void => {
                notifyAboutException({
                    toast: toast,
                    content: reason.data,
                });
            });
    };

    const onSuccessLessonMaterialUploading: VoidFunction = (): void => {
        notifyAboutSuccess({
            toast: toast,
            content: 'Материал урока успешно обновлен',
        });

        setLessonMaterialModalVisible(false);
    };

    const onErrorWhenLessonMaterialUploading: ReasonFunction = (
        reason: any
    ): void => {
        notifyAboutException({
            toast: toast,
            content: reason.data,
        });
    };

    const onLessonMaterialFormSubmit: FormSubmitter<LessonMaterial<Date>> = (
        form: LessonMaterial<Date>
    ): void => {
        if (!selectedLesson) return;

        uploadLessonMaterial({
            path: {
                lessonId: selectedLesson.id,
            },
            body: {
                materialId: form.material.id,
                expiresAt: form.expiresAt?.getTime() || null,
            },
        })
            .unwrap()
            .then(onSuccessLessonMaterialUploading)
            .catch(onErrorWhenLessonMaterialUploading);
    };

    const onLessonCardClick: SingleParamFunction<TeacherLesson<Dayjs>> = (
        lesson: TeacherLesson<Dayjs>
    ): void => {
        if (lesson.id === selectedLesson?.id) return;

        selectLesson(lesson);
        setStudentsAttendance([]);
    };

    const onLessonMarkingSuccessfullyFinished: VoidFunction = (): void => {
        setLessons((previous: TeacherLesson<Dayjs>[]): TeacherLesson<Dayjs>[] =>
            filter(previous, (lesson: TeacherLesson<Dayjs>): boolean => {
                return lesson.id === selectedLesson?.id;
            })
        );

        selectLesson(null);

        notifyAboutSuccess({
            toast: toast,
            content: 'Отметка урока успешно закончена!',
        });
    };

    const onErrorWhenLessonMarkingFinishing: ReasonFunction = (
        reason: any
    ): void => {
        notifyAboutException({
            toast: toast,
            content: reason.data,
        });
    };

    const onLessonMarkingFinish: VoidFunction = (): void => {
        if (!canFinishLessonMarking) {
            return notifyAboutException({
                sound: true,
                content:
                    'Нельзя завершить отметку занятия, пока не отмечены все студенты',
            });
        }

        if (!selectedLesson) return;

        finishMarking({
            path: { lessonId: selectedLesson.id },
        })
            .unwrap()
            .then(onLessonMarkingSuccessfullyFinished)
            .catch(onErrorWhenLessonMarkingFinishing);
    };

    return (
        <StudentLayout auth={props}>
            <Toast ref={toast} />
            <Helmet title={'Активные занятия'} />
            <Card
                header={'Активные занятия'}
                className={'mt-24'}
                headerClassName={styles.header}
                contentClassName={'p-24'}
            >
                {selectedLesson && lessonMaterial && (
                    <LegacyModal
                        header={'Методический материал'}
                        visible={isLessonMaterialModalVisible}
                        onHide={(): void =>
                            setLessonMaterialModalVisible(false)
                        }
                    >
                        <LessonMaterialAttachingForm
                            lessonId={selectedLesson.id}
                            initialValues={lessonMaterial}
                            onSubmit={onLessonMaterialFormSubmit}
                        />
                    </LegacyModal>
                )}

                {size(lessons) ? (
                    teacherLessonsResponseIsLoading ? (
                        <Loader />
                    ) : (
                        <>
                            <div className={styles.nearestLessonsSlider}>
                                {map(
                                    lessons,
                                    (
                                        lesson: TeacherLesson<Dayjs>
                                    ): ReactElement => {
                                        return (
                                            <TeacherLessonCard
                                                key={lesson.id}
                                                className={classNames(
                                                    styles.nearestLesson,
                                                    {
                                                        [styles.many]:
                                                            size(lessons) > 2,
                                                    }
                                                )}
                                                lesson={lesson}
                                                isSelected={
                                                    lesson.id ===
                                                    selectedLesson?.id
                                                }
                                                onClick={onLessonCardClick}
                                            />
                                        );
                                    }
                                )}
                            </div>

                            {selectedLesson ? (
                                isLessonAttachedMaterialIsLoading ? (
                                    <Loader />
                                ) : (
                                    <>
                                        <FormItem label={'Методический пакет'}>
                                            <Button
                                                onClick={(): void =>
                                                    setLessonMaterialModalVisible(
                                                        true
                                                    )
                                                }
                                            >
                                                {lessonHasMaterial
                                                    ? 'Обновить'
                                                    : 'Загрузить'}
                                            </Button>
                                        </FormItem>

                                        {lessonHasMaterial && (
                                            <>
                                                {studentsAttendanceResponseIsLoading ? (
                                                    <Loader />
                                                ) : (
                                                    <>
                                                        {size(
                                                            studentsAttendance
                                                        ) ? (
                                                            <>
                                                                <AttendanceTable
                                                                    statusLoading={
                                                                        studentsAttendanceResponseIsLoading
                                                                    }
                                                                    attendance={
                                                                        studentsAttendance
                                                                    }
                                                                    onAttendanceChange={
                                                                        onStudentAttendanceChanged
                                                                    }
                                                                />
                                                            </>
                                                        ) : null}
                                                        <ConfirmButton
                                                            loading={
                                                                isFinishing
                                                            }
                                                            onConfirm={
                                                                onLessonMarkingFinish
                                                            }
                                                        />
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </>
                                )
                            ) : null}
                        </>
                    )
                ) : (
                    'Нет ближайших уроков'
                )}
            </Card>
        </StudentLayout>
    );
};
